import React, { useState, useEffect } from 'react';
import { t, getCurrentLanguage } from '../utils/translations';
import { useNavigate } from 'react-router-dom';
import api from './api';
import { CardLoader } from './Loader';

const StrategySection = () => {
  const [strategies, setStrategies] = useState([]);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const navigate = useNavigate();

  // Fonction pour nettoyer le HTML et tronquer le texte
  const cleanAndTruncateHtml = (htmlText, maxLength = 120) => {
    if (!htmlText) return '';
    
    // Créer un élément temporaire pour parser le HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlText;
    
    // Extraire le texte brut sans HTML
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    
    // Tronquer le texte
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength).trim() + '...';
  };

  // Écouter les changements de langue
  useEffect(() => {
    const handleLanguageChange = () => {
      const newLang = getCurrentLanguage();
      setCurrentLanguage(newLang);
    };

    // Écouter l'événement de changement de langue
    window.addEventListener('languageChanged', handleLanguageChange);
    
    // Écouter aussi les changements i18n
    const i18nListener = () => {
      const newLang = getCurrentLanguage();
      setCurrentLanguage(newLang);
    };
    
    // Écouter les changements de langue i18n
    if (window.i18n) {
      window.i18n.on('languageChanged', i18nListener);
    }
    
    // Écouter aussi les changements de localStorage
    const storageListener = () => {
      const newLang = getCurrentLanguage();
      setCurrentLanguage(newLang);
    };
    
    window.addEventListener('storage', storageListener);
    
    // Vérifier la langue actuelle toutes les 100ms (solution temporaire)
    const interval = setInterval(() => {
      const currentLang = getCurrentLanguage();
      if (currentLang !== currentLanguage) {
        setCurrentLanguage(currentLang);
      }
    }, 100);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      if (window.i18n) {
        window.i18n.off('languageChanged', i18nListener);
      }
      window.removeEventListener('storage', storageListener);
      clearInterval(interval);
    };
  }, [currentLanguage]);

  // Récupérer les stratégies depuis l'API
  useEffect(() => {
    const fetchStrategies = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/strategies/');
        
        if (response.data && response.data.length > 0) {
          setStrategies(response.data);
          setError(null);
        } else {
          // Aucune stratégie trouvée
          setStrategies([]);
          setError(null);
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des stratégies:', err);
        setError('Erreur lors du chargement des stratégies');
        setStrategies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStrategies();
  }, []);

  // Récupérer les 3 derniers articles depuis l'API
  useEffect(() => {
    const fetchRecentArticles = async () => {
      try {
        setArticlesLoading(true);
        // Utiliser l'API avec pagination pour récupérer seulement les 3 premiers articles
        const response = await api.get('/api/articles/', {
          params: {
            page: 1,
            page_size: 3,
            sort: '-published_at' // Plus récents en premier
          }
        });
        
        if (response.data && response.data.results) {
          setRecentArticles(response.data.results);
        } else {
          // Aucun article trouvé en base
          setRecentArticles([]);
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des articles:', err);
        setRecentArticles([]);
      } finally {
        setArticlesLoading(false);
      }
    };

    fetchRecentArticles();
  }, []);

  return (
    <>
      {/* Strategy Section */}
      <section className="relative py-6 bg-gradient-to-br from-slate-100 via-gray-100 to-blue-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          {/* Title Section */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {t('strategy.title')}
            </h2>
            <p className="text-gray-600 text-sm max-w-xl mx-auto">
              {t('strategy.subtitle')}
            </p>
            <div className="w-16 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] mx-auto mt-3 rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {loading ? (
              // Loader pour toutes les stratégies
              <>
                <CardLoader />
                <CardLoader />
                <CardLoader />
                <CardLoader />
                <CardLoader />
              </>
            ) : error ? (
              <div className="col-span-full text-center py-12">
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="h-12 w-12 text-red-500 mx-auto mb-4">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <p className="text-red-600 text-lg font-medium mb-2">{t('strategy.loadingError')}</p>
                  <p className="text-gray-500 text-sm mb-4">{error}</p>
                  <button 
                    onClick={() => window.location.reload()} 
                    className="bg-[#089556] hover:bg-[#07D1EB] text-white px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200"
                  >
                    {t('strategy.retry')}
                  </button>
                </div>
              </div>
            ) : strategies.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="h-12 w-12 text-gray-400 mx-auto mb-4">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <p className="text-gray-600 text-lg font-medium mb-2">{t('strategy.noStrategies')}</p>
                  <p className="text-gray-500 text-sm">{t('strategy.noStrategiesDesc')}</p>
                </div>
              </div>
            ) : (
              strategies.map((strategy) => {
                const getContentByLanguage = (field) => {
                  if (currentLanguage === 'en') {
                    return strategy[`${field}_en`] || strategy[`${field}_fr`] || '';
                  }
                  return strategy[`${field}_fr`] || strategy[`${field}_en`] || '';
                };

                const title = getContentByLanguage('title');
                const image = getContentByLanguage('image');

                return (
                  <div
                    key={strategy.id}
                    onClick={() => {
                      if (strategy.slug) {
                        const basePath = currentLanguage === 'en' ? '/en' : '/fr';
                        navigate(`${basePath}/full-description/${strategy.slug}`);
                      }
                    }}
                    className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full aspect-square p-3 bg-gray-50">
                      <img 
                        src={image || strategy.image_url || strategy.image}
                        alt={title || 'Stratégie'}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgNzBDMTE2LjU2OSA3MCAxMzAgODMuNDMxIDEzMCAxMDBDMTMwIDExNi41NjkgMTE2LjU2OSAxMzAgMTAwIDEzMEM4My40MzEgMTMwIDcwIDExNi41NjkgNzAgMTAwQzcwIDgzLjQzMSA4My40MzEgNzAgMTAwIDcwWiIgZmlsbD0iIzlDQ0E1NTYiLz4KPC9zdmc+';
                        }}
                      />
                    </div>
                    {title && (
                      <div className="p-2 text-center">
                        <p className="text-xs font-medium text-gray-700 line-clamp-1">{title}</p>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* Recent Articles Section */}
      <section className="relative py-6 bg-gradient-to-br from-[#07D1EB] via-[#089556] to-[#0ba8c7] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          {/* Title Section */}
          <div className="text-center mb-6">
            <h2 className="text-white text-2xl font-bold mb-2">
              {t('strategy.recentArticlesTitle')}
            </h2>
            <p className="text-white/90 text-sm max-w-xl mx-auto">
              {t('strategy.recentArticlesSubtitle')}
            </p>
            <div className="w-16 h-1 bg-white/60 mx-auto mt-3 rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {articlesLoading ? (
              // Loader pour les articles
              <>
                <CardLoader />
                <CardLoader />
                <CardLoader />
              </>
            ) : recentArticles.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 border border-white/30">
                  <div className="h-12 w-12 text-white/60 mx-auto mb-4">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                    </svg>
                  </div>
                  <p className="text-white/80 text-lg font-medium mb-2">{t('strategy.noArticles')}</p>
                  <p className="text-white/60 text-sm">{t('strategy.noArticlesDesc')}</p>
                </div>
              </div>
            ) : (
              recentArticles.map((article) => {
                // Sélectionner le contenu selon la langue actuelle
                const getContentByLanguage = (field) => {
                  if (currentLanguage === 'en') {
                    return article[`${field}_en`] || article[`${field}_fr`] || '';
                  }
                  return article[`${field}_fr`] || article[`${field}_en`] || '';
                };

                const translatedTitle = getContentByLanguage('title');
                const translatedDescription = getContentByLanguage('description');
                const translatedCategory = article.category_info ? 
                  (currentLanguage === 'en' ? article.category_info.libelle_en : article.category_info.libelle_fr) : 
                  'Article';

                return (
                  <div
                    key={article.id}
                    onClick={() => navigate(`/${currentLanguage}/actualite/${article.slug}`)}
                    className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 flex flex-col h-[360px] cursor-pointer"
                  >
                    {/* Image de couverture */}
                    <div className="relative w-full h-40 overflow-hidden flex-shrink-0">
                      <img
                        src={article.cover_image_url || article.cover_image || '/images/article-default.jpg'}
                        alt={translatedTitle}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = '/images/article-default.jpg';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                      
                      {/* Badge catégorie */}
                      <div className="absolute top-2 left-2">
                        <span className="text-xs text-white bg-[#089556]/90 px-2 py-0.5 rounded-full font-medium">
                          {translatedCategory}
                        </span>
                      </div>
                    </div>

                    {/* Contenu de l'article */}
                    <div className="p-4 flex flex-col flex-1">
                      <h3 className="text-gray-800 text-base font-bold mb-2 group-hover:text-[#089556] transition-colors line-clamp-2">
                        {translatedTitle}
                      </h3>
                      
                      <p className="text-gray-600 text-xs leading-relaxed line-clamp-3 flex-1">
                        {translatedDescription ? cleanAndTruncateHtml(translatedDescription, 120) : t('strategy.noDescription')}
                      </p>

                      {/* Lien "Savoir plus" */}
                      <div className="mt-3 pt-2 border-t border-gray-100">
                        <span className="inline-flex items-center text-xs text-[#089556] font-medium group-hover:text-[#07D1EB] transition-colors">
                          {t('strategy.readMore')}
                          <svg className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default StrategySection;